'use client'

import Link from 'next/link'
import { useRouter, useSearchParams } from 'next/navigation'
import { useTranslations } from 'next-intl'
import { FC, useCallback, useState } from 'react'
import { useForm } from 'react-hook-form'

import { AlertErrorComponent } from '@/components/alert/alert-error'
import { PasswordInput } from '@/components/common/form/password-input'
import { VariantFormLabel } from '@/components/common/form/variant-form-label'
import { Button } from '@/components/ui/button'
import { Form, FormControl, FormField, FormItem, FormMessage } from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import { useToast } from '@/components/ui/use-toast'

import { verifyLocale } from '@/hooks/locale-switcher'
import { useZodResolver } from '@/hooks/use-zod-resolver'
import { LoginSchema } from '@/schema/auth-schema'
import { authSignInByEmailService } from '@/services/auth-service'
import { createAuthBearerToken, getAuthSession } from '@/services/auth-session-service'

export const AuthLoginFormComponent: FC = () => {
  const tFormCurrent = useTranslations('Auth.forms.login_form')
  const tFormFields = useTranslations('Auth.forms.login_form.fields')
  const resolver = useZodResolver({ schema: LoginSchema, tFormFields })
  const router = useRouter()
  const searchParams = useSearchParams()
  const { toast } = useToast()
  const [isLoading, setIsLoading] = useState(false)
  const [loginError, setLoginError] = useState<string | null>(null)

  const emailFromSearchParams = searchParams?.get('email')
  const form = useForm<typeof LoginSchema._type>({
    resolver,
    defaultValues: {
      email: emailFromSearchParams || '',
      password: '',
    },
  })

  const onSubmit = useCallback(
    async (values: typeof LoginSchema._type) => {
      setLoginError(null)
      setIsLoading(true)

      const { email, password } = values

      const { ok, error, data } = await authSignInByEmailService({ email, password })

      if (ok) {
        const { authToken } = data
        if (!authToken) {
          throw new Error('Auth token not found, make sure bearer plugin is properly configured')
        }

        // generate token and store it in cookies
        await createAuthBearerToken(authToken)
        await getAuthSession({ forceLatest: true })
        // upon sign in, start to verify locale
        await verifyLocale()

        toast({
          variant: 'success',
          description: tFormCurrent('messages.submit_success'),
        })

        router.push('/dashboard')
      }

      setTimeout(() => {
        setIsLoading(false)
      }, 100) /// delay to show toast

      if (error) {
        const isUserNotConfirmed = error === 'EMAIL_NOT_VERIFIED'
        // const isHardLockedUser = result.error === 'CredentialsSignin' && result.code === 'hard_locked_user'

        // if (isHardLockedUser) {
        //   form.setError('email', {
        //     type: 'manual',
        //     message: tFormCommon('messages.user_hard_locked'),
        //   })
        //   return
        // }

        return setLoginError(
          isUserNotConfirmed ? tFormCurrent('messages.user_not_confirmed') : tFormCurrent('messages.submit_error'),
        )
      }
    },
    [setLoginError, setIsLoading, toast, tFormCurrent, verifyLocale, router, form],
  )

  return (
    <>
      {loginError && <AlertErrorComponent description={loginError} />}

      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="mt-2 space-y-8">
          <FormField
            control={form.control}
            name="email"
            render={({ field }) => (
              <FormItem>
                <VariantFormLabel>{tFormFields('email')}</VariantFormLabel>
                <FormControl>
                  <Input {...field} placeholder={tFormCurrent('placeholders.email')} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="password"
            render={({ field }) => (
              <FormItem>
                <div className="flex items-center justify-between">
                  <VariantFormLabel>{tFormFields('password')}</VariantFormLabel>
                  <Button
                    asChild
                    variant="noneHover"
                    className="justify-start text-muted-foreground underline"
                    size="sm"
                  >
                    <Link href="/auth/forgot-password">{tFormCurrent('actions.forgot_password')}</Link>
                  </Button>
                </div>
                <FormControl>
                  <PasswordInput {...field} placeholder={tFormCurrent('placeholders.password')} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <Button type="submit" className="w-full" disabled={isLoading}>
            {tFormCurrent('actions.form_submit')}
          </Button>
        </form>
      </Form>
    </>
  )
}
